import { useMemo, useState } from 'react';

import BottomSheet from './BottomSheet';
import { cn } from '@/lib/utils';
import { ALL_REACTIONS_TAB, orderReactors, reactionTabs } from '@/utils/reactions';
import { useTranslation } from '@/utils/i18n';
import type { ReachViewer, ReactionUserEntry } from '@/types';

export function Avatar({ user, size = 'tw:size-9' }: { user: ReachViewer; size?: string }) {
  if (user.avatar_url) {
    return (
      <img
        src={user.avatar_url}
        alt=""
        className={cn(size, 'tw:shrink-0 tw:rounded-full tw:object-cover')}
      />
    );
  }
  return (
    <span
      className={cn(
        size,
        'tw:flex tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full',
        'tw:bg-blue-primary tw:text-[12px] tw:font-semibold tw:text-white',
      )}
    >
      {(user.username ?? '?').slice(0, 2).toUpperCase()}
    </span>
  );
}

/**
 * C2 — who reacted to a recommendation, Instagram-style.
 *
 * A horizontally scrollable tab strip ("All" + one tab per emoji with its
 * count) filters a single scrollable list of reactors, ordered me → friends →
 * everybody else. `initialEmoji` lets a tapped emoji chip on the card or in
 * "My Recos" open the sheet already filtered to that emoji.
 *
 * When the backend sent no reactor rows (legacy payload) it degrades to the
 * old counts-only list, so an outdated cache never renders an empty sheet.
 */
export default function ReactionsSheet({
  open,
  title,
  entries,
  users,
  meId,
  initialEmoji,
  onClose,
}: {
  open: boolean;
  title: string;
  entries: [string, number][];
  /** C2 — who reacted (one row per user+emoji). Falls back to counts-only. */
  users?: ReactionUserEntry[];
  /** Puts the viewer at the top of the list. */
  meId?: number | null;
  /** Preselects the tab of the emoji the user tapped. */
  initialEmoji?: string | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<string>(initialEmoji ?? ALL_REACTIONS_TAB);

  // Re-arm the requested tab whenever the sheet is (re)opened on another emoji:
  // one instance serves every chip on the card. Adjusted during render — the
  // React-recommended alternative to a setState-in-effect, which would paint
  // the previous tab for a frame first.
  const [armed, setArmed] = useState<string | null>(null);
  const wanted = `${open}:${initialEmoji ?? ''}`;
  if (armed !== wanted) {
    setArmed(wanted);
    if (open) setTab(initialEmoji ?? ALL_REACTIONS_TAB);
  }

  const tabs = useMemo(() => reactionTabs(entries, users), [entries, users]);
  const ordered = useMemo(() => orderReactors(users ?? [], meId), [users, meId]);
  const visible = useMemo(
    () => (tab === ALL_REACTIONS_TAB ? ordered : ordered.filter((e) => e.emoji === tab)),
    [ordered, tab],
  );
  // reco-sheet-per-gesture — the tab count is global, the list names only the
  // viewer's circle: the difference is the people who reacted from elsewhere.
  const others = Math.max(0, (tabs.find(([e]) => e === tab)?.[1] ?? 0) - visible.length);

  const hasUsers = ordered.length > 0;

  return (
    <BottomSheet open={open} title={title} onClose={onClose} testid="reactions-sheet">
      {hasUsers ? (
        <>
          {/* Tab strip — one row, scrolls sideways when a reco collects many emojis. */}
          {tabs.length > 2 && (
            <div
              data-testid="reactions-sheet-tabs"
              className="tw:-mx-1 tw:mb-2 tw:flex tw:gap-2 tw:overflow-x-auto tw:px-1 tw:pb-1"
            >
              {tabs.map(([emoji, count]) => {
                const active = tab === emoji;
                const isAll = emoji === ALL_REACTIONS_TAB;
                return (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => setTab(emoji)}
                    data-testid={`reactions-sheet-tab-${isAll ? 'all' : emoji}`}
                    aria-pressed={active}
                    className={cn(
                      'tw:flex tw:shrink-0 tw:items-center tw:gap-1.5 tw:rounded-full tw:px-3 tw:py-1.5',
                      'tw:text-[13px] tw:font-semibold tw:transition-colors',
                      active
                        ? 'tw:bg-blue-primary tw:text-white'
                        : 'tw:bg-dark-primary tw:text-grey-2',
                    )}
                  >
                    <span className={isAll ? 'tw:text-[13px]' : 'tw:text-[17px]'}>
                      {isAll ? t('reactionsSheet.all') : emoji}
                    </span>
                    <span>{count}</span>
                  </button>
                );
              })}
            </div>
          )}

          <ul className="tw:flex tw:flex-col tw:gap-1">
            {visible.map(({ emoji, user }) => (
              <li
                key={`${user.id}-${emoji}`}
                data-testid={`reactions-sheet-user-${user.id}`}
                className="tw:flex tw:items-center tw:gap-3 tw:rounded-xl tw:px-2 tw:py-2"
              >
                <Avatar user={user} />
                <span className="tw:flex tw:min-w-0 tw:flex-1 tw:items-center tw:gap-2">
                  <span className="tw:truncate tw:text-[15px] tw:text-white">{user.username}</span>
                  {meId != null && user.id === meId && (
                    <span className="tw:shrink-0 tw:rounded-full tw:bg-blue-primary/25 tw:px-2 tw:py-0.5 tw:text-[11px] tw:text-blue-primary">
                      {t('reactionsSheet.you')}
                    </span>
                  )}
                </span>
                <span className="tw:shrink-0 tw:text-[22px]">{emoji}</span>
              </li>
            ))}
          </ul>
          {others > 0 && (
            <p
              data-testid="reactions-sheet-others"
              className="tw:px-2 tw:pt-2 tw:text-[13px] tw:text-grey-3"
            >
              {t('reactionsSheet.others', { count: others })}
            </p>
          )}
        </>
      ) : (
        // reco-sheet-per-gesture — nobody from this circle reacted: the backend
        // sent the reactions without identities. Say how many, from elsewhere.
        // (Replaces the counts-only list written for pre-2.1.0 payloads; this
        // line stays true for those too.)
        others > 0 && (
          <p
            data-testid="reactions-sheet-others"
            className="tw:px-2 tw:py-2 tw:text-[15px] tw:text-grey-2"
          >
            {t('reactionsSheet.othersOnly', { count: others })}
          </p>
        )
      )}
    </BottomSheet>
  );
}
